import { Directive, Input, Output, HostListener, EventEmitter } from '@angular/core';
import { TypeReject, TypeResolve } from '@libs/constants';


/**------------------------------------------------------
 * Drag and drop
 * --------------------
 * The user can drag and drop files over the container and
 * can utilize it for functionalities like handling upload,
 * stat of drag and drop of elements, etc.
 *
 * Example:
 * --------
 * 	<div ngDragDrop (isOverContainer)='...'> ... </div>
 */
@Directive({
    selector: '[ngDragDrop]',
    standalone: false
})
export class NgDragDropDirective {

	@Input() maxSize : number = Number.MAX_SAFE_INTEGER;
	@Input() maxCount: number = Number.MAX_SAFE_INTEGER;

	@Output() readonly filesDropped		: EventEmitter<FileList> = new EventEmitter<FileList>();
	@Output() readonly draggedOverEvent	: EventEmitter<void>	 = new EventEmitter<void>();
	@Output() readonly errorEvent		: EventEmitter<string>   = new EventEmitter<string>();


	@HostListener('dragover', ['$event'])
	public onDragOver(event: Event): void {
		this.draggedOverEvent.emit();
		this.stopEventRipple(event);
	}

	@HostListener('dragleave', ['$event'])
	public onDragLeave(event: Event): void {
		this.stopEventRipple(event);
	}

	@HostListener('drop', ['$event'])
	public onDrop(event: DragEvent): void {

		//0 - clear items from data transfer
		// this.data.clearData();
		this.stopEventRipple(event);

		//1 - get dropped files and emit it
		const dataTransfer	: DataTransfer = new DataTransfer();
		const  dataSize		: number = 0;
		this.getFiles(event.dataTransfer!.items, dataTransfer, dataSize).then((files: FileList) => {
			this.filesDropped.emit(files);
			event = new DragEvent('dragend');
		});
	}

	//* get the dragged file(s) or from folder
	getFiles(dataTransferItems: DataTransferItemList, dataTransfer: DataTransfer, dataSize: number): Promise<FileList> {
		return new Promise((resolve: TypeResolve<FileList>) => {
			const entriesPromises: Promise<(File | INgFile)[]>[] = [];
			for (const item of dataTransferItems) {
				entriesPromises.push(this.traverseFileTreePromise(item.webkitGetAsEntry(), '', dataTransfer, dataSize));
			}
			Promise.all(entriesPromises)
				.then(() => {
					resolve(dataTransfer.files);
				})
				.catch(() => {
					this.errorEvent.emit(`Uploaded Size has exceeded ${(this.maxSize / 1000000).toFixed(0)}MB`);
				});
		});
	}


	/**------------------------------------------------------
	 * Traverse the dropped folder structure recursively
	 * -------------------------------------------------
	 * > Getting files from folders: https://stackoverflow.com/questions/3590058/does-html5-allow-drag-drop-upload-of-folders-or-a-folder-tree
	 */
	private traverseFileTreePromise(item: FileSystemEntry | FileSystemFileEntry | FileSystemDirectoryEntry | null, path: string = '', dataTransfer: DataTransfer, dataSize: number): Promise<(File | INgFile)[]> {
		return new Promise((resolve: TypeResolve<(File | INgFile)[]>, reject: TypeReject) => {

			//0 - add file to data transfer
			if (item!.isFile) {
				(item! as FileSystemFileEntry).file((file: File | INgFile) => {

					//a. get hte file
					(file as INgFile).filepath = path + file.name;
					dataTransfer.items.add(file);
					dataSize += file.size;

					//b. is the limit reached
					if (dataTransfer.items.length > this.maxCount) reject();
					if (dataSize > this.maxSize) reject();

					//c. add the file
					resolve([file]);
				});
				return;
			}

			//1 - traverse through directory and add files to the data transfer
			if (item!.isDirectory) {
				const dirReader: FileSystemDirectoryReader = (item! as FileSystemDirectoryEntry).createReader();
				dirReader.readEntries((entries: (FileSystemEntry | FileSystemFileEntry | FileSystemDirectoryEntry)[]) => {
					const entriesPromises: Promise<(File | INgFile)[]>[] = [];
					for (const entry of entries) entriesPromises.push(this.traverseFileTreePromise(entry, `${path + item!.name}/`, dataTransfer, dataSize));
					const allPromises: Promise<(File | INgFile)[]> = Promise.all(entriesPromises).then((filesArray: (File | INgFile)[][]) => filesArray.reduce((acc: (File | INgFile)[], files: (File | INgFile)[]) => acc.concat(files), []));
					resolve(allPromises);
				});
			}
		});
	}

	//* Handle event bubbling */
	private stopEventRipple(event: Event) {
		event.preventDefault();
		event.stopPropagation();
	}
}


//** Interfaces --------------------------------- */
interface INgFile extends File {
	filepath: string;
}
